import { PerformanceGradeValues } from '@forgd/shared/leaderboard/performanceGrade'
import z from 'zod'

export const LeaderboardForMmPathParamsSchema = z.object({
  marketMakerId: z.string(),
})

export const LeaderboardQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  projectId: z.string().optional(), // this is only used for app portal to define if project have access to the leaderboard
  integrationStatus: z.enum(['full', 'partial', 'none']).optional(),
})

const LeaderboardItemSchema = z.object({
  value: z.number().nullable(),
  percentile: z.number().min(0).max(100),
  rank: z.number().int().min(1),
})

const SharedLeaderboardRecordSchema = z.object({
  rank: LeaderboardItemSchema.pick({ value: true, percentile: true }),
  marketMakerId: z.string(),
  marketMakerName: z.string(),
  marketMakerImageUrl: z.string().nullable(),
})

// Aggregated leaderboard
const AggregatedLeaderboardItemSchema = SharedLeaderboardRecordSchema.extend({
  bidAskDepth: LeaderboardItemSchema,
  bidAskDepthMarketPercentage: LeaderboardItemSchema,
  depthLoanUtilization: LeaderboardItemSchema,
  dailyFillVolume: LeaderboardItemSchema,
  dailyFillVolumeMarketPercentage: LeaderboardItemSchema,
  volumeLoanUtilization: LeaderboardItemSchema,
  topOfBookSpread: LeaderboardItemSchema,
  combinedUptime: LeaderboardItemSchema,
})

export type AggregatedLeaderboardRecordType = z.infer<typeof AggregatedLeaderboardItemSchema>

export const AggregatedLeaderboardResponseSchema = z.array(AggregatedLeaderboardItemSchema)

// Depth leaderboard
const DepthLeaderboardItemSchema = SharedLeaderboardRecordSchema.extend({
  totalRank: LeaderboardItemSchema.pick({ value: true, percentile: true }),
  bidDepth: LeaderboardItemSchema,
  bidDepthMarketPercentage: LeaderboardItemSchema,
  askDepth: LeaderboardItemSchema,
  askDepthMarketPercentage: LeaderboardItemSchema,
  bidAskDepth: LeaderboardItemSchema,
  bidAskDepthMarketPercentage: LeaderboardItemSchema,
  depthLoanUtilization: LeaderboardItemSchema,
  uptime: LeaderboardItemSchema,
})

export const DepthLeaderboardResponseSchema = z.object({
  depth50: z.array(DepthLeaderboardItemSchema),
  depth100: z.array(DepthLeaderboardItemSchema),
  depth200: z.array(DepthLeaderboardItemSchema),
})

export type DepthLeaderboardRecordType = z.infer<typeof DepthLeaderboardItemSchema>

// Volume leaderboard
const VolumeLeaderboardItemSchema = SharedLeaderboardRecordSchema.extend({
  orderFillVolumeMaker: LeaderboardItemSchema,
  orderFillVolumeMakerMarketPercentage: LeaderboardItemSchema,
  orderFillVolumeTaker: LeaderboardItemSchema,
  orderFillVolumeTakerMarketPercentage: LeaderboardItemSchema,
  orderFillVolumeTotal: LeaderboardItemSchema,
  orderFillVolumeTotalMarketPercentage: LeaderboardItemSchema,
  volumeLoanUtilization: LeaderboardItemSchema,
})

export type VolumeLeaderboardRecordType = z.infer<typeof VolumeLeaderboardItemSchema>

export const VolumeLeaderboardResponseSchema = z.array(VolumeLeaderboardItemSchema)

// Spread leaderboard
const SpreadLeaderboardItemSchema = SharedLeaderboardRecordSchema.extend({
  bidAskSpread: LeaderboardItemSchema,
  uptime: LeaderboardItemSchema,
})

export type SpreadLeaderboardRecordType = z.infer<typeof SpreadLeaderboardItemSchema>

export const SpreadLeaderboardResponseSchema = z.array(SpreadLeaderboardItemSchema)

// Kpi leaderboard
const KpiLeaderboardItemSchema = SharedLeaderboardRecordSchema.extend({
  bidAskDepth50: LeaderboardItemSchema,
  bidAskDepth100: LeaderboardItemSchema,
  bidAskDepth200: LeaderboardItemSchema,
  bidAskSpread: LeaderboardItemSchema,
  uptime: LeaderboardItemSchema,
})

export type KpiLeaderboardRecordType = z.infer<typeof KpiLeaderboardItemSchema>

export const KpiLeaderboardResponseSchema = z.array(KpiLeaderboardItemSchema)

export const LeaderboardBreakdownResponseSchema = z.object({
  aggregatedRank: LeaderboardItemSchema.pick({ value: true, percentile: true }).nullable(),
  depthRank: LeaderboardItemSchema.pick({ value: true, percentile: true }).nullable(),
  volumeRank: LeaderboardItemSchema.pick({ value: true, percentile: true }).nullable(),
  spreadRank: LeaderboardItemSchema.pick({ value: true, percentile: true }).nullable(),
  kpiRank: LeaderboardItemSchema.pick({ value: true, percentile: true }).nullable(),

  bidDepth50: LeaderboardItemSchema,
  askDepth50: LeaderboardItemSchema,
  bidAskDepth50: LeaderboardItemSchema,
  bidDepth50MarketPercentage: LeaderboardItemSchema,
  askDepth50MarketPercentage: LeaderboardItemSchema,
  bidAskDepth50MarketPercentage: LeaderboardItemSchema,
  bidAskDepth50Uptime: LeaderboardItemSchema,
  bidAskDepth50LoanUtilization: LeaderboardItemSchema,

  bidDepth100: LeaderboardItemSchema,
  askDepth100: LeaderboardItemSchema,
  bidAskDepth100: LeaderboardItemSchema,
  bidDepth100MarketPercentage: LeaderboardItemSchema,
  askDepth100MarketPercentage: LeaderboardItemSchema,
  bidAskDepth100MarketPercentage: LeaderboardItemSchema,
  bidAskDepth100Uptime: LeaderboardItemSchema,
  bidAskDepth100LoanUtilization: LeaderboardItemSchema,

  bidDepth200: LeaderboardItemSchema,
  askDepth200: LeaderboardItemSchema,
  bidAskDepth200: LeaderboardItemSchema,
  bidDepth200MarketPercentage: LeaderboardItemSchema,
  askDepth200MarketPercentage: LeaderboardItemSchema,
  bidAskDepth200MarketPercentage: LeaderboardItemSchema,
  bidAskDepth200Uptime: LeaderboardItemSchema,
  bidAskDepth200LoanUtilization: LeaderboardItemSchema,

  volumeMaker: LeaderboardItemSchema,
  volumeTaker: LeaderboardItemSchema,
  volumeTotal: LeaderboardItemSchema,
  volumeMakerMarketPercentage: LeaderboardItemSchema,
  volumeTakerMarketPercentage: LeaderboardItemSchema,
  volumeTotalMarketPercentage: LeaderboardItemSchema,
  volumeMakerLoanUtilization: LeaderboardItemSchema,
  volumeTakerLoanUtilization: LeaderboardItemSchema,
  volumeTotalLoanUtilization: LeaderboardItemSchema,

  bidAskSpread: LeaderboardItemSchema,
  spreadUptime: LeaderboardItemSchema,

  combinedUptime: LeaderboardItemSchema,
})

export type LeaderboardBreakdownRecordType = z.infer<typeof LeaderboardBreakdownResponseSchema>

// Performance metrics
const PerformanceMetricsItemSchema = z.object({
  marketMakerId: z.string(),
  marketMakerName: z.string(),
  marketMakerImageUrl: z.string().nullable(),
  tradingKpis: z.number().nullable(),
  trustIntegration: z.number(),
  coverageCapabilities: z.number(),
  uptime: z.number().nullable(),
  integrationLevel: z.number(),
  integrationStatus: z.enum(['full', 'partial', 'none']),
  composite: z.number(),
  grade: z.enum(PerformanceGradeValues),
  activeEngagementsCount: z.number(),
  medianEngagementsFdv: z.number().nullable(),
})

// Admin raw performance metrics (no overrides, never masked)
export type RawPerformanceMetricsType = z.infer<typeof PerformanceMetricsItemSchema>

// Public performance metrics (with overrides applied); quant nulls composite and grade on masked CCC rows
export const PerformanceMetricsForMmResponseSchema = PerformanceMetricsItemSchema.extend({
  composite: z.number().nullable(),
  grade: z.enum(PerformanceGradeValues).nullable(),
})
export type PerformanceMetricsForMmType = z.infer<typeof PerformanceMetricsForMmResponseSchema>
